import { useMemo, useState } from "react";
import { Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatPriceCents } from "@/components/services/service-meta";
import { BotaoAdicionar } from "./BotaoAdicionar";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";
import { AdicionarDaBaseDialog } from "@/components/catalogo/base/AdicionarDaBaseDialog";
import { CatalogoBusca, combina } from "./CatalogoBusca";
import { EquipamentoDialog, type EquipamentoEditTarget } from "./EquipamentoDialog";

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";

/**
 * Equipamentos do laboratório — fresadora, impressora, forno.
 *
 * Existe pra responder "por que a broca acabou". A ficha técnica sabia o que a peça consome, mas
 * não por qual máquina ela passou nem que fresar dissilicato desgasta a broca diferente de fresar
 * resina. Aqui se cadastra esse gasto, por material; ele entra no custo da ficha e sai do estoque
 * quando o trabalho conclui.
 */
export function EquipamentoSection() {
  const utils = trpc.useUtils();
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState(false);
  const [daBase, setDaBase] = useState(false);
  // Vindo do "Complete seu laboratório": já abre o Adicionar da base.
  useParametroDaUrl("adicionar", (v) => v === "equipamentos" && setDaBase(true));
  const [editando, setEditando] = useState<EquipamentoEditTarget | null>(null);

  const listaQuery = trpc.equipamento.listar.useQuery();
  const ofertasQuery = trpc.prosthesis.catalog.materialOptions.useQuery();

  const setAtivo = trpc.equipamento.setAtivo.useMutation({
    onSuccess: () => utils.equipamento.listar.invalidate(),
    onError: (e) => toast.error(e.message || "Erro ao atualizar equipamento."),
  });
  const remover = trpc.equipamento.remover.useMutation({
    onSuccess: () => {
      toast.success("Equipamento excluído.");
      utils.equipamento.listar.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir equipamento."),
  });

  const rotuloDoMaterial = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of ofertasQuery.data ?? []) m.set(o.id, o.label);
    return m;
  }, [ofertasQuery.data]);

  const lista = (listaQuery.data ?? []).filter(
    (e: any) =>
      combina(e.name, busca) ||
      combina(e.notes, busca) ||
      e.materialOptionIds.some((id: string) => combina(rotuloDoMaterial.get(id), busca)),
  );

  const ocupado = setAtivo.isPending || remover.isPending;

  function abrirNovo() {
    setEditando(null);
    setAberto(true);
  }

  return (
    <div id="equipamentos" className={cardCls}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 data-tour="catalogo-gerenciar-secao-titulo" className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            Equipamentos
          </h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            Suas máquinas e o que cada uma gasta de consumível, por material. O gasto entra no custo
            da ficha técnica e sai do estoque quando o trabalho conclui.
          </p>
        </div>
        <BotaoAdicionar topo onClick={() => setDaBase(true)}>
          Adicionar equipamento
        </BotaoAdicionar>
        <AdicionarDaBaseDialog tipo="equipamentos" open={daBase} onClose={() => setDaBase(false)} onCriarNovo={abrirNovo} />
      </div>

      <CatalogoBusca
        valor={busca}
        onChange={setBusca}
        placeholder="Buscar por equipamento ou material"
        achados={lista.length}
      />

      <div className="mt-3 flex flex-col gap-2">
        {lista.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-zinc-400 dark:text-muted-foreground">
            {busca.trim()
              ? "Nenhum equipamento com essa busca."
              : "Nenhum equipamento ainda. Cadastre a fresadora, a impressora ou o forno pra saber quanto de broca cada trabalho come."}
          </p>
        ) : (
          lista.map((e: any) => (
            <div
              key={e.id}
              onClick={
                ocupado
                  ? undefined
                  : () => {
                      setEditando(e);
                      setAberto(true);
                    }
              }
              className={cn(
                "flex items-start gap-2 rounded-lg border border-zinc-100 p-3 transition-colors dark:border-border/60",
                !ocupado && "cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/5",
                !e.active && "opacity-60",
              )}
            >
              <span onClick={(ev) => ev.stopPropagation()} className="pt-0.5">
                <Checkbox
                  checked={e.active}
                  disabled={ocupado}
                  onCheckedChange={(v) => setAtivo.mutate({ equipmentId: e.id, ativo: v === true })}
                  aria-label={`Ativar/desativar ${e.name}`}
                />
              </span>

              <button
                type="button"
                disabled={ocupado}
                aria-label={`Editar ${e.name}`}
                className="min-w-0 flex-1 text-left disabled:opacity-50"
                onClick={(ev) => {
                  ev.stopPropagation();
                  setEditando(e);
                  setAberto(true);
                }}
              >
                <p className="flex items-center gap-1.5 text-sm font-medium text-zinc-900 dark:text-foreground">
                  <Wrench className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                  {e.name}
                </p>
                <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {e.materialOptionIds.length === 0
                    ? "Vale para qualquer material"
                    : e.materialOptionIds
                        .map((id: string) => rotuloDoMaterial.get(id) ?? "—")
                        .join(", ")}
                </p>
                {e.gastos.length > 0 && (
                  <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-zinc-600 dark:text-foreground/80">
                    {Object.entries(e.custoPorMaterial).map(([id, cents]) => (
                      <span key={id}>
                        <span className="text-zinc-400">
                          {id === "__padrao__" ? "Padrão" : (rotuloDoMaterial.get(id) ?? "—")}:
                        </span>{" "}
                        <span className="tabular-nums font-medium">{formatPriceCents(cents as number)}</span>
                      </span>
                    ))}
                  </p>
                )}
                {e.gastos.length === 0 && (
                  <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    Sem gasto cadastrado — não soma nada na ficha ainda.
                  </p>
                )}
              </button>

              <span onClick={(ev) => ev.stopPropagation()}>
                <button
                  type="button"
                  disabled={ocupado}
                  aria-label={`Excluir ${e.name}`}
                  title="Excluir"
                  onClick={() => {
                    if (window.confirm(`Excluir o equipamento "${e.name}"?`)) {
                      remover.mutate({ equipmentId: e.id });
                    }
                  }}
                  className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </div>
          ))
        )}
      </div>

      <EquipamentoDialog
        open={aberto}
        onClose={() => {
          setAberto(false);
          setEditando(null);
        }}
        editTarget={editando}
      />
    </div>
  );
}
